import { queryOptions, useQuery } from "@tanstack/react-query";
import * as z from "zod";
import { api } from "../http";
import { paginatedListSchema } from "../utils/zod";

export const INCIDENT_SEVERITY = ["Low", "Medium", "High", "Critical"] as const;
export const incidentSeveritySchema = z.enum(INCIDENT_SEVERITY);
export type IncidentSeverity = z.infer<typeof incidentSeveritySchema>;

export const INCIDENT_TYPE = [
  "SECURE_LLM",
  "SLACK",
  "GOOGLE_DRIVE",
  "EMAIL_DLP",
  "DESKTOP_OVERLAY",
  "GATEWAY",
] as const;
export const incidentTypeSchema = z.enum(INCIDENT_TYPE);
export type IncidentType = z.infer<typeof incidentTypeSchema>;

export const INCIDENT_STATUS = [
  "OPEN",
  "ASSIGNED",
  "IN_REVIEW",
  "RESOLVED",
  "FALSE_POSITIVE",
] as const;
export const incidentStatusSchema = z.enum(INCIDENT_STATUS);
export type IncidentStatus = z.infer<typeof incidentStatusSchema>;

const userReferenceSchema = z
  .object({
    id: z.string(),
    full_name: z.string(),
    email: z.string(),
  })
  .nullable();

const incidentSubjectSchema = z
  .object({
    kind: z.enum(["pii", "guardrail"]),
    label: z.string(),
    detail: z.string().nullable().optional(),
    criticality: z.string().nullable().optional(),
    confidence: z.number().nullable().optional(),
    action: z.string().nullable().optional(),
  })
  .nullable();

const incidentContextSchema = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("gateway"),
      gateway_key_id: z.string().nullable().optional(),
      gateway_key_name: z.string().nullable().optional(),
      model: z.string().nullable().optional(),
      request_id: z.string().nullable().optional(),
    }),
    z.object({
      kind: z.literal("desktop"),
      app_name: z.string().nullable().optional(),
      website: z.string().nullable().optional(),
    }),
    z.object({
      kind: z.literal("chat"),
      chat_id: z.string().nullable().optional(),
    }),
    z.object({
      kind: z.literal("generic"),
      label: z.string().nullable().optional(),
    }),
  ])
  .nullable();

export const incidentSchema = z.object({
  id: z.string(),
  status: incidentStatusSchema,
  severity: incidentSeveritySchema,
  type: incidentTypeSchema,
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime().nullable(),
  deleted_at: z.iso.datetime().nullable(),
  object_mapping_type: z.string().nullable().optional(),
  object_id: z.string().nullable().optional(),
  triggered_by_user: userReferenceSchema,
  assigned_by_user: userReferenceSchema,
  assigned_to_user: userReferenceSchema,
  message_entity: z
    .object({
      id: z.string(),
      entity_text: z.string(),
      masked_entity_text: z.string().nullable(),
      confidence_score: z.number(),
      is_critical_pii: z.boolean(),
      criticality_level: z.string(),
      entity_type: z
        .object({
          id: z.number(),
          name: z.string(),
          description: z.string().nullable().optional(),
        })
        .nullable(),
    })
    .nullable(),
  guardrail_violation: z
    .object({
      id: z.string(),
      guardrail_feat_id: z.number(),
      message_id: z.string(),
      content_type: z.string(),
      details: z.record(z.string(), z.unknown()),
      created_at: z.string(),
    })
    .nullable()
    .optional(),
  gateway_key_id: z.string().nullable().optional(),
  subject: incidentSubjectSchema.optional(),
  context: incidentContextSchema.optional(),
});

export const incidentsListSchema = paginatedListSchema(incidentSchema);

export type Incident = z.infer<typeof incidentSchema>;
export type IncidentsList = z.infer<typeof incidentsListSchema>;

type SearchParams = {
  page: number;
  per_page?: number;
  status?: IncidentStatus;
  severity?: IncidentSeverity;
  type?: IncidentType;
  triggered_by?: string;
  assigned_to?: string;
  assigned_by?: string;
  start_date?: string;
  end_date?: string;
  order_by?: string;
};

export function incidentsListQueryOptions(params: SearchParams) {
  return queryOptions({
    queryKey: ["incidents", "list", params],
    queryFn() {
      const searchParams = new URLSearchParams({
        page: params.page.toString(),
        per_page: (params.per_page || 10).toString(),
      });

      if (params.status) searchParams.set("status", params.status);
      if (params.severity) searchParams.set("severity", params.severity);
      if (params.type) searchParams.set("type", params.type);
      if (params.triggered_by)
        searchParams.set("triggered_by", params.triggered_by);
      if (params.assigned_to)
        searchParams.set("assigned_to", params.assigned_to);
      if (params.assigned_by)
        searchParams.set("assigned_by", params.assigned_by);
      if (params.start_date) searchParams.set("start_date", params.start_date);
      if (params.end_date) searchParams.set("end_date", params.end_date);
      if (params.order_by) searchParams.set("order_by", params.order_by);

      return api.raw
        .get("api/v1/incidents/", {
          searchParams,
        })
        .json<IncidentsList>();
    },
  });
}

export function incidentQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["incidents", "detail", id],
    queryFn() {
      return api.raw.get(`api/v1/incidents/${id}`).json<Incident>();
    },
  });
}

type IncidentStatusOptions = {
  statuses: Array<string>;
  descriptions: Record<string, string>;
};
export function incidentStatusOptionsQueryOptions() {
  return queryOptions({
    queryKey: ["incidents", "status-options"],
    queryFn() {
      return api.raw
        .get("api/v1/incidents/status-options")
        .json<IncidentStatusOptions>();
    },
  });
}

type IncidentFilterOptions = {
  statuses: Array<string>;
  severities: Array<string>;
  types: Array<string>;
  order_fields: Array<string>;
  order_examples: Array<string>;
};

export function incidentFilterOptionsQueryOptions() {
  return queryOptions({
    queryKey: ["incidents", "filter-options"],
    queryFn() {
      return api.raw
        .get("api/v1/incidents/filter-options")
        .json<IncidentFilterOptions>();
    },
  });
}

export function useIncidentsList(params: SearchParams) {
  return useQuery(incidentsListQueryOptions(params));
}

export function useIncident(id: string) {
  return useQuery(incidentQueryOptions(id));
}

export function useIncidentStatusOptions() {
  return useQuery(incidentStatusOptionsQueryOptions());
}

export function useIncidentFilterOptions() {
  return useQuery(incidentFilterOptionsQueryOptions());
}
